import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Calendar as CalendarIcon, Clock, MapPin, ChevronLeft, ChevronRight, Loader2, ExternalLink, RefreshCw, AlertCircle, Pencil, Trash2, Check, ChevronsUpDown , Search } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { eventosAPI, expedientesAPI } from '../api';
import { toast } from '../hooks/use-toast';

// Función helper para parsear fechas en cualquier formato
const parseEventDate = (dateValue) => {
  if (!dateValue) return null;
  
  // Si ya es un objeto Date válido
  if (dateValue instanceof Date && !isNaN(dateValue)) {
    return dateValue;
  }
  
  // Convertir a string si no lo es
  const dateStr = String(dateValue);
  
  // Intentar parseo directo (ISO format)
  let date = new Date(dateStr);
  if (!isNaN(date.getTime())) {
    return date;
  }
  
  // Formato: "YYYY-MM-DD HH:MM:SS" (MySQL/MariaDB)
  const mysqlMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);
  if (mysqlMatch) {
    const [, year, month, day, hour, min, sec] = mysqlMatch;
    return new Date(year, month - 1, day, hour, min, sec);
  }
  
  // Formato: "DD/MM/YYYY HH:MM" (Español)
  const esMatch = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})$/);
  if (esMatch) {
    const [, day, month, year, hour, min] = esMatch;
    return new Date(year, month - 1, day, hour, min);
  }
  
  // Formato: "DD/MM/YYYY" (Solo fecha)
  const esDateOnly = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (esDateOnly) {
    const [, day, month, year] = esDateOnly;
    return new Date(year, month - 1, day);
  }
  
  // Formato timestamp Unix (segundos o milisegundos)
  if (/^\d+$/.test(dateStr)) {
    const num = parseInt(dateStr);
    // Si es menor que año 3000 en milisegundos, asumir segundos
    return new Date(num < 100000000000 ? num * 1000 : num);
  }
  
  console.warn('[Agenda] No se pudo parsear fecha:', dateValue);
  return null;
};

// Combobox buscable para expedientes - usa búsqueda en servidor (sin cmdk)
const ExpedienteCombobox = ({ expedientes: initialExpedientes, value, onChange, name, disabled }) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimeout = React.useRef(null);
  const inputRef = React.useRef(null);

  const displayList = searchQuery.length >= 2 ? searchResults : initialExpedientes;
  const allKnown = React.useMemo(() => {
    const map = new Map();
    initialExpedientes.forEach(e => map.set(String(e.id), e));
    searchResults.forEach(e => map.set(String(e.id), e));
    return map;
  }, [initialExpedientes, searchResults]);
  const selected = value ? allKnown.get(String(value)) : null;

  React.useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (query.length < 2) { setSearchResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await expedientesAPI.getAll({ search: query, limit: 50 });
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.expedientes || []);
        setSearchResults(data);
      } catch (e) { console.error('Error buscando expedientes:', e); }
      setSearching(false);
    }, 300);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal" disabled={disabled} type="button" data-testid="expediente-combobox-trigger">
          <span className="truncate flex-1 text-left min-w-0" title={selected ? `${selected.numero_expediente} - ${selected.titulo}` : ''}>{selected ? `${selected.numero_expediente} - ${selected.titulo}` : 'Buscar expediente...'}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      {name && <input type="hidden" name={name} value={value || ''} />}
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="flex items-center border-b px-3 py-2">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input
            ref={inputRef}
            type="text"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
            placeholder="Buscar por numero o nombre..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            data-testid="expediente-search-input"
          />
          {searchQuery && (
            <button type="button" className="ml-1 text-gray-400 hover:text-gray-600 text-xs" onClick={() => { setSearchQuery(''); setSearchResults([]); }}>
              &times;
            </button>
          )}
        </div>
        <div className="max-h-[280px] overflow-y-auto" data-testid="expediente-results-list">
          {searching && <div className="p-3 text-sm text-center text-gray-500">Buscando...</div>}
          {!searching && displayList.length === 0 && (
            <div className="p-4 text-sm text-center text-gray-500">
              {searchQuery.length < 2 ? 'Escribe al menos 2 caracteres...' : 'No se encontraron expedientes'}
            </div>
          )}
          {displayList.map(exp => (
            <div
              key={exp.id}
              className={`flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-accent transition-colors ${String(value) === String(exp.id) ? 'bg-accent/50' : ''}`}
              onClick={() => { onChange(String(exp.id)); setOpen(false); setSearchQuery(''); setSearchResults([]); }}
              data-testid={`expediente-option-${exp.id}`}
            >
              <Check className={`mr-2 h-4 w-4 flex-shrink-0 ${String(value) === String(exp.id) ? 'opacity-100' : 'opacity-0'}`} />
              <span className="truncate">{exp.numero_expediente} - {exp.titulo}</span>
            </div>
          ))}
        </div>
        {value && (
          <div className="border-t px-3 py-2">
            <button type="button" className="text-xs text-red-500 hover:text-red-700" onClick={() => { onChange(''); setOpen(false); setSearchQuery(''); setSearchResults([]); }}
              data-testid="expediente-clear-btn">
              Quitar expediente
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

const Agenda = () => {
  const navigate = useNavigate();
  const [eventos, setEventos] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createExpedienteId, setCreateExpedienteId] = useState('');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingEvento, setEditingEvento] = useState(null);
  const [editForm, setEditForm] = useState({
    titulo: "", tipo: "", fecha: "", hora: "", fecha_fin: "", hora_fin: "",
    expediente_id: "", responsable: "", notas: "", estado: "pendiente"
  });

  const loadData = useCallback(async () => {
    console.log('[Agenda] Iniciando carga de datos...');
    try {
      setLoading(true);
      setError(null);
      
      // Cargar eventos. Pedimos limit alto para evitar que citas próximas
      // queden fuera del top-N por culpa de paginación (Antes el backend
      // limitaba a 100 y las citas no aparecían en el calendario).
      console.log('[Agenda] Llamando a eventosAPI.getAll()...');
      const eventosRes = await eventosAPI.getAll({ limit: 5000 });
      console.log('[Agenda] Respuesta eventos raw:', eventosRes);
      
      // Manejar diferentes formatos de respuesta
      let eventosData = [];
      if (eventosRes?.data) {
        if (Array.isArray(eventosRes.data)) {
          eventosData = eventosRes.data;
        } else if (eventosRes.data.data && Array.isArray(eventosRes.data.data)) {
          eventosData = eventosRes.data.data;
        }
      }
      console.log('[Agenda] Eventos procesados:', eventosData.length);
      setEventos(eventosData);
      
      // Cargar expedientes
      console.log('[Agenda] Llamando a expedientesAPI.getAll()...');
      const expedientesRes = await expedientesAPI.getAll();
      console.log('[Agenda] Respuesta expedientes raw:', expedientesRes);
      
      let expData = [];
      if (expedientesRes?.data) {
        if (Array.isArray(expedientesRes.data)) {
          expData = expedientesRes.data;
        } else if (expedientesRes.data.data && Array.isArray(expedientesRes.data.data)) {
          expData = expedientesRes.data.data;
        }
      }
      console.log('[Agenda] Expedientes procesados:', expData.length);
      setExpedientes(expData);
      
      console.log('[Agenda] Carga completada exitosamente');
      
    } catch (err) {
      console.error('[Agenda] Error cargando datos:', err);
      console.error('[Agenda] Error response:', err.response);
      setError(err.message || 'Error al cargar los datos');
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los eventos. Revisa la consola para más detalles.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateEvento = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const expedienteId = createExpedienteId;
    
    const fechaStr = formData.get('fecha');
    const horaStr = formData.get('hora') || '09:00';
    const fechaInicio = new Date(`${fechaStr}T${horaStr}:00`);
    const fechaFin = new Date(fechaInicio.getTime() + 60 * 60 * 1000);
    
    const newEvento = {
      titulo: formData.get('titulo'),
      expediente_id: expedienteId ? parseInt(expedienteId) : null,
      fecha_inicio: fechaInicio.toISOString(),
      fecha_fin: fechaFin.toISOString(),
      calendario: formData.get('tipo')?.toLowerCase() || 'reunion',
      tipo: formData.get('tipo'),
      estado: 'pendiente',
      todo_el_dia: false,
      descripcion: formData.get('notas') || '',
      asignados: formData.get('responsable') || ''
    };
    
    try {
      await eventosAPI.create(newEvento);
      await loadData();
      setIsCreateDialogOpen(false);
      toast({
        title: '¡Evento creado!',
        description: `${newEvento.titulo} añadido al calendario`,
      });
    } catch (error) {
      console.error('Error creando evento:', error);
      toast({
        title: 'Error',
        description: 'No se pudo crear el evento',
        variant: 'destructive'
      });
    }
  };

  const handleEditClick = (evento, e) => {
    e?.stopPropagation();
    setEditingEvento(evento);
    const fechaInicio = parseEventDate(evento.fecha_inicio);
    const fechaFin = parseEventDate(evento.fecha_fin);
    setEditForm({
      titulo: evento.titulo || '',
      tipo: evento.calendario || evento.tipo || '',
      fecha: fechaInicio ? fechaInicio.toISOString().split('T')[0] : '',
      hora: fechaInicio ? fechaInicio.toTimeString().slice(0, 5) : '',
      fecha_fin: fechaFin ? fechaFin.toISOString().split('T')[0] : '',
      hora_fin: fechaFin ? fechaFin.toTimeString().slice(0, 5) : '',
      expediente_id: evento.expediente_id ? String(evento.expediente_id) : '',
      responsable: evento.asignados || '',
      notas: evento.descripcion || '',
      estado: evento.estado || 'pendiente'
    });
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e?.preventDefault?.();
    const fechaInicio = editForm.fecha + 'T' + (editForm.hora || '00:00') + ':00';
    const fechaFin = editForm.fecha_fin ? (editForm.fecha_fin + 'T' + (editForm.hora_fin || '23:59') + ':00') : null;
    const body = {
      titulo: editForm.titulo,
      calendario: (editForm.tipo || 'reunion').toLowerCase(),
      tipo: editForm.tipo,
      estado: editForm.estado,
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin,
      expediente_id: editForm.expediente_id ? parseInt(editForm.expediente_id) : null,
      asignados: editForm.responsable || null,
      descripcion: editForm.notas || null
    };
    try {
      const res = await eventosAPI.update(editingEvento.id, body);
      console.log('UPDATE OK:', res);
      toast({ title: 'Evento actualizado' });
      setIsEditDialogOpen(false);
      setEditingEvento(null);
      loadData();
    } catch (error) {
      console.error('UPDATE FAIL:', error);
      toast({ title: 'Error al actualizar', description: String(error.response?.data?.detail || error.message), variant: 'destructive' });
    }
  };

  const handleDeleteEvento = async (evento, e) => {
    e?.stopPropagation();
    const esCita = evento.tipo === 'cita_cliente';
    const aviso = esCita
      ? '\u00bfEliminar "' + evento.titulo + '"?\n\nSe borrar\u00e1 tambi\u00e9n la cita en Tareas del Despacho.'
      : '\u00bfEliminar el evento "' + evento.titulo + '"?';
    if (!window.confirm(aviso)) return;
    try {
      const res = await eventosAPI.delete(evento.id);
      const citas = res?.data?.citas_eliminadas || 0;
      toast({
        title: 'Evento eliminado',
        description: citas ? 'Tambi\u00e9n se ha borrado la cita del despacho.' : undefined
      });
      await loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.response?.data?.detail || 'No se pudo eliminar el evento',
        variant: 'destructive'
      });
    }
  };

  const getEventosByDate = (date) => {
    if (!Array.isArray(eventos)) return [];
    return eventos.filter(evento => {
      if (!evento?.fecha_inicio) return false;
      const eventoDate = parseEventDate(evento.fecha_inicio);
      if (!eventoDate) return false;
      return eventoDate.toDateString() === date.toDateString();
    });
  };

  const getTipoBadge = (tipo) => {
    const colors = {
      'audiencia': 'bg-red-100 text-red-800',
      'audiencia_previa': 'bg-red-100 text-red-800',
      'reunion': 'bg-blue-100 text-blue-800',
      'plazo': 'bg-orange-100 text-orange-800',
      'conciliacion': 'bg-purple-100 text-purple-800',
      'juicio': 'bg-red-200 text-red-900',
      'vista': 'bg-pink-100 text-pink-800'
    };
    const tipoLower = (tipo || '').toLowerCase();
    return colors[tipoLower] || 'bg-gray-100 text-gray-800';
  };

  const formatTipo = (tipo) => {
    if (!tipo) return 'Evento';
    return tipo.charAt(0).toUpperCase() + tipo.slice(1).replace(/_/g, ' ');
  };

  const changeMonth = (increment) => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + increment);
    setSelectedDate(newDate);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const daysInMonth = getDaysInMonth(selectedDate);
  const eventosHoy = getEventosByDate(selectedDate);
  const todosEventos = Array.isArray(eventos) ? [...eventos].sort((a, b) => {
    const dateA = parseEventDate(a.fecha_inicio);
    const dateB = parseEventDate(b.fecha_inicio);
    if (!dateA || !dateB) return 0;
    return dateA - dateB;
  }) : [];
  const eventosProximos = todosEventos.filter(e => {
    const fecha = parseEventDate(e.fecha_inicio);
    return fecha && fecha >= new Date();
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 space-y-4">
        <AlertCircle className="w-12 h-12 text-red-500" />
        <p className="text-red-600 font-medium">{error}</p>
        <Button onClick={loadData} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agenda</h1>
          <p className="text-gray-600 mt-1">Gestiona eventos, citas y plazos ({eventos.length} eventos)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Evento
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle>Crear Nuevo Evento</DialogTitle>
                <DialogDescription>
                  Añade un evento al calendario
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateEvento}>
                <div className="grid grid-cols-1 gap-4 py-4">
                  <div>
                    <Label htmlFor="titulo">Título del evento</Label>
                    <Input id="titulo" name="titulo" required />
                  </div>
                  <div>
                    <Label htmlFor="tipo">Tipo de evento</Label>
                    <Select name="tipo" required>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="audiencia">Audiencia</SelectItem>
                        <SelectItem value="audiencia_previa">Audiencia Previa</SelectItem>
                        <SelectItem value="reunion">Reunión</SelectItem>
                        <SelectItem value="plazo">Plazo</SelectItem>
                        <SelectItem value="conciliacion">Conciliación</SelectItem>
                        <SelectItem value="juicio">Juicio</SelectItem>
                        <SelectItem value="vista">Vista</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="expediente">Expediente asociado</Label>
                    <ExpedienteCombobox expedientes={expedientes} value={createExpedienteId} onChange={setCreateExpedienteId} name="expediente" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="fecha">Fecha</Label>
                      <Input id="fecha" name="fecha" type="date" required />
                    </div>
                    <div>
                      <Label htmlFor="hora">Hora</Label>
                      <Input id="hora" name="hora" type="time" defaultValue="09:00" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="responsable">Responsable</Label>
                    <Input id="responsable" name="responsable" placeholder="Nombre del responsable" />
                  </div>
                  <div>
                    <Label htmlFor="notas">Notas</Label>
                    <Textarea id="notas" name="notas" placeholder="Observaciones o detalles adicionales" />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    Crear Evento
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2">
          <CardContent className="p-6">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-6">
              <Button variant="ghost" size="icon" onClick={() => changeMonth(-1)} data-testid="calendar-prev-month">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <h2 className="text-lg font-semibold">
                {selectedDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
              </h2>
              <Button variant="ghost" size="icon" onClick={() => changeMonth(1)} data-testid="calendar-next-month">
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>

            {/* Days of Week */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {daysInMonth.map((day, index) => {
                const dayEventos = day ? getEventosByDate(day) : [];
                const isToday = day && day.toDateString() === new Date().toDateString();
                const isSelected = day && day.toDateString() === selectedDate.toDateString();
                
                return (
                  <div
                    key={index}
                    className={`min-h-[80px] p-1 border rounded-lg cursor-pointer transition-colors
                      ${!day ? 'bg-gray-50' : 'hover:bg-gray-50'}
                      ${isToday ? 'border-blue-500 bg-blue-50' : ''}
                      ${isSelected ? 'ring-2 ring-blue-500' : ''}
                    `}
                    onClick={() => day && setSelectedDate(day)}
                  >
                    {day && (
                      <>
                        <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : ''}`}>
                          {day.getDate()}
                        </div>
                        <div className="space-y-1">
                          {dayEventos.slice(0, 2).map(evento => (
                            <div
                              key={evento.id}
                              className={`text-xs p-1 rounded truncate cursor-pointer hover:opacity-80 ${getTipoBadge(evento.calendario || evento.tipo)}`}
                              title={`${evento.titulo} - Clic para editar`}
                              onClick={(e) => { e.stopPropagation(); handleEditClick(evento, e); }}
                            >
                              {evento.titulo}
                            </div>
                          ))}
                          {dayEventos.length > 2 && (
                            <div className="text-xs text-gray-500">
                              +{dayEventos.length - 2} más
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Sidebar - Próximos Eventos */}
        <div className="space-y-4">
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold mb-4">Próximos Eventos</h3>
              {eventosProximos.length === 0 ? (
                <p className="text-gray-500 text-sm">No hay eventos próximos</p>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {eventosProximos.slice(0, 10).map(evento => {
                    const fecha = parseEventDate(evento.fecha_inicio);
                    const expediente = evento.expediente_numero
                      ? { id: evento.expediente_id, numero_expediente: evento.expediente_numero }
                      : expedientes.find(e => e.id === evento.expediente_id);
                    if (!fecha) return null;
                    return (
                      <div 
                        key={evento.id} 
                        className="flex gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                        onClick={(e) => handleEditClick(evento, e)}
                      >
                        <div className="flex-shrink-0 w-12 text-center">
                          <div className="text-2xl font-bold text-gray-800">
                            {fecha.getDate()}
                          </div>
                          <div className="text-xs text-gray-500 uppercase">
                            {fecha.toLocaleDateString('es-ES', { month: 'short' })}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{evento.titulo}</div>
                          {expediente && (
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/expedientes/${evento.expediente_id}`);
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 hover:underline truncate flex items-center gap-1"
                            >
                              {expediente.numero_expediente}
                              <ExternalLink className="w-3 h-3" />
                            </button>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={`text-xs ${getTipoBadge(evento.calendario || evento.tipo)}`}>
                              {formatTipo(evento.calendario || evento.tipo)}
                            </Badge>
                            {evento.fecha_inicio && (
                              <span className="text-xs text-gray-500 flex items-center">
                                <Clock className="w-3 h-3 mr-1" />
                                {fecha.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button onClick={(e) => handleEditClick(evento, e)} className="p-1 hover:bg-gray-200 rounded" title="Editar" data-testid={`next-edit-event-${evento.id}`}>
                            <Pencil className="w-3.5 h-3.5 text-blue-600" />
                          </button>
                          <button onClick={(e) => handleDeleteEvento(evento, e)} className="p-1 hover:bg-gray-200 rounded" title="Eliminar" data-testid={`next-delete-event-${evento.id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-red-600" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Eventos del día seleccionado */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold mb-4">
                Eventos del {selectedDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}
              </h3>
              {eventosHoy.length === 0 ? (
                <p className="text-gray-500 text-sm">No hay eventos para este día</p>
              ) : (
                <div className="space-y-2">
                  {eventosHoy.map(evento => {
                    const fecha = parseEventDate(evento.fecha_inicio);
                    const expediente = evento.expediente_numero
                      ? { id: evento.expediente_id, numero_expediente: evento.expediente_numero }
                      : expedientes.find(e => e.id === evento.expediente_id);
                    if (!fecha) return null;
                    return (
                      <div 
                        key={evento.id} 
                        className="p-2 border rounded-lg cursor-pointer hover:bg-gray-50"
                        onClick={(e) => handleEditClick(evento, e)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm">{evento.titulo}</div>
                        {expediente && (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/expedientes/${evento.expediente_id}`);
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 mt-1"
                          >
                            {expediente.numero_expediente}
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        )}
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                          <Clock className="w-3 h-3" />
                          {fecha.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                              <Badge className={`ml-auto ${getTipoBadge(evento.calendario || evento.tipo)}`}>
                                {formatTipo(evento.calendario || evento.tipo)}
                              </Badge>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                            <button onClick={(e) => handleEditClick(evento, e)} className="p-1 hover:bg-gray-200 rounded" title="Editar" data-testid={`day-edit-event-${evento.id}`}>
                              <Pencil className="w-3.5 h-3.5 text-blue-600" />
                            </button>
                            <button onClick={(e) => handleDeleteEvento(evento, e)} className="p-1 hover:bg-gray-200 rounded" title="Eliminar" data-testid={`day-delete-event-${evento.id}`}>
                              <Trash2 className="w-3.5 h-3.5 text-red-600" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={isEditDialogOpen} onOpenChange={(open) => { setIsEditDialogOpen(open); if (!open) setEditingEvento(null); }}>
        <DialogContent className="max-w-4xl w-[92vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Evento</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit}>
            <div className="grid grid-cols-1 gap-4 py-4">
              <div>
                <Label htmlFor="edit-titulo">Título del evento</Label>
                <Input id="edit-titulo" value={editForm.titulo} onChange={(e) => setEditForm(prev => ({...prev, titulo: e.target.value}))} required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo de evento</Label>
                  <Select value={editForm.tipo} onValueChange={(v) => setEditForm(prev => ({...prev, tipo: v}))}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar tipo" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="audiencia">Audiencia</SelectItem>
                      <SelectItem value="audiencia_previa">Audiencia Previa</SelectItem>
                      <SelectItem value="reunion">Reunión</SelectItem>
                      <SelectItem value="plazo">Plazo</SelectItem>
                      <SelectItem value="conciliacion">Conciliación</SelectItem>
                      <SelectItem value="juicio">Juicio</SelectItem>
                      <SelectItem value="vista">Vista</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Estado</Label>
                  <Select value={editForm.estado} onValueChange={(v) => setEditForm(prev => ({...prev, estado: v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pendiente">Pendiente</SelectItem>
                      <SelectItem value="completada">Completada</SelectItem>
                      <SelectItem value="pospuesta">Pospuesta</SelectItem>
                      <SelectItem value="cancelada">Cancelada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Expediente asociado</Label>
                <ExpedienteCombobox expedientes={expedientes} value={editForm.expediente_id} onChange={(v) => setEditForm(prev => ({...prev, expediente_id: v}))} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha inicio</Label>
                  <div className="flex gap-2 mt-1">
                    <Input type="date" value={editForm.fecha} onChange={(e) => setEditForm(prev => ({...prev, fecha: e.target.value}))} required className="flex-1" />
                    <Input type="time" value={editForm.hora} onChange={(e) => setEditForm(prev => ({...prev, hora: e.target.value}))} className="w-28" />
                  </div>
                </div>
                <div>
                  <Label>Fecha fin</Label>
                  <div className="flex gap-2 mt-1">
                    <Input type="date" value={editForm.fecha_fin} onChange={(e) => setEditForm(prev => ({...prev, fecha_fin: e.target.value}))} className="flex-1" />
                    <Input type="time" value={editForm.hora_fin} onChange={(e) => setEditForm(prev => ({...prev, hora_fin: e.target.value}))} className="w-28" />
                  </div>
                </div>
              </div>
              <div>
                <Label>Responsable</Label>
                <Input value={editForm.responsable} onChange={(e) => setEditForm(prev => ({...prev, responsable: e.target.value}))} placeholder="Nombre del responsable" />
              </div>
              <div>
                <Label>Notas</Label>
                <Textarea value={editForm.notas} onChange={(e) => setEditForm(prev => ({...prev, notas: e.target.value}))} placeholder="Observaciones o detalles" className="min-h-[180px] resize-y" />
              </div>
            </div>
            <DialogFooter>
              {editingEvento && (
                <Button type="button" variant="destructive" onClick={(e) => { handleDeleteEvento(editingEvento, e); setIsEditDialogOpen(false); }} className="mr-auto">
                  <Trash2 className="w-4 h-4 mr-2" />Eliminar
                </Button>
              )}
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancelar</Button>
              <Button type="button" className="bg-blue-600 hover:bg-blue-700" onClick={handleEditSubmit}>Guardar cambios</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Agenda;